// 계약 런칭 프로그램 타임라인 보드 — 드래그 이동 · 양끝 리사이즈 · 생성/삭제 · 줌 3단 · undo 1단 · 편집 팝오버.
// Design: docs/02-design/contract-timeline.design.md §2(소유권) / §5.2(결정 20) / §8.2(범례) / §9-Q5(v1 스코프).
// 불변: 계약 4축(기간·데모·인허가·MOQ쿼터)은 contracts.* 소유 = **읽기전용 배경 레인**. 이 파일에 계약 쓰기 경로가 없다(§2 — 이 설계의 핵심 안전장치).
// 불변: 기하·드래그 의미론은 @/lib/timeline 순수함수 단일 출처. 날짜 산술·픽셀 환산을 여기서 재구현하지 않는다(결정1·7).
// 불변: 포인터는 setPointerCapture 로만 추적(전역 리스너 0, 결정12) · 드래그 중 디바운스 금지 · pointerup 1회 커밋(결정16) · pointercancel 롤백(결정15의 대가).
// 불변: 낙관적 업데이트는 반드시 롤백 쌍과 함께 — 실패 = 빨간 배너 + 보드 락(§7.1). 회색 토스트 금지.
// 테스트 잠금: 컴포넌트 테스트 인프라 없음(§5.1) → 드래그 정확성은 scale.test.ts / drag.test.ts 가 순수함수로 잠근다. 여기서 잠글 것은 없다.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isSupabaseConfigured,
  timelineEventsRepo,
  timelineLanesRepo,
  type NewTimelineEvent,
} from "@/lib/db";
import { resolveDrag, type DragMode, type Span } from "@/lib/timeline/drag";
import {
  addDays,
  barPct,
  dateToPct,
  dayToPct,
  fmtShort,
  fromDayIndex,
  makeScale,
  milestonePct,
  monthTicks,
  toDayIndex,
  type Scale,
} from "@/lib/timeline/scale";
import type {
  ContractRow,
  ContractTimelineEvent,
  Deal,
  TimelineEmphasis,
  TimelineKind,
  TimelineLane,
} from "@/types";
import { btn } from "./ui";

// ── 레이아웃 상수 ────────────────────────────────────────────────────────────
/** 좌측 라벨 열 폭(px). 그리드 오버레이가 같은 값으로 좌단을 맞춘다.
 *  이름 + **기간 데이터 열**(DATE_W)을 함께 담는다 — 바 안에서 날짜를 뺀 대가(§8.2 재정의). */
const LABEL_W = 296;
/** 라벨 열 안의 기간 표기 폭(px) — mono 10px 로 "26.12.20→27.02.07"(17자) 가 들어가는 최소치. */
const DATE_W = 104;
/** 행 높이(px) — 시안(30px)보다 한 뼘 여유. */
const ROW_H = 34;
/** 눈금 헤더 높이(px) — 상단=오늘 칩 / 하단=월 라벨 2단. */
const RULER_H = 30;
/** 게이트 라벨 스트립 높이(px) — 게이트가 하나도 없으면 렌더하지 않는다. */
const GATE_H = 20;
/** 클릭 vs 드래그 임계(px) — 이하는 클릭(팝오버 열기) (§5.2). */
const CLICK_PX = 4;
/** 이 폭 미만의 바는 **핸들을 제거**한다(본체 드래그만) — 빗맞은 리사이즈 = 조용한 손상(결정14). */
const HANDLE_MIN_PX = 16;
/** 저장중 표시 지연(ms) — 즉시 표시는 깜빡임만 만든다(결정18). */
const SAVING_DELAY_MS = 250;
/** 신규 이벤트 기본 길이(일, 양끝 포함). */
const NEW_DAYS = 7;
/** 이 일수 이하의 바는 **마일스톤형 글리프**(캡+다이아)로 그린다 — 데이터는 여전히 bar(드래그·리사이즈 그대로).
 *  근거: 전체 줌에서 4일 = 약 17px → 20px 높이의 정사각 덩어리가 되어 "기간"이 아니라 노이즈로 읽힌다. */
const SHORT_BAR_DAYS = 7;

// ── 시각 토큰 (§8.2 — 대비 실측이 결정했다. brass 는 인터랙션 전용) ──────────
// "제도(製圖)" 방향: 잉크=구조 · 브라스=계기 · 모노=계측값. 바에는 텍스트가 없다 — 형태만 남긴다.
/** 바 높이(px) — 높이도 위계를 말하지만, **길이를 이기지는 못한다**(7개월 primary 가 2개월 critical 을 압도했다). */
const EMPH_H: Record<TimelineEmphasis, number> = { critical: 22, primary: 18, support: 12 };
// 위계는 **명도가 아니라 채도·질감**이 만든다.
// 바에서 텍스트를 데이터 열로 빼낸 뒤로 채움색에 AA 4.5:1 제약이 사라졌다(흰 텍스트를 얹지 않는다)
// → primary 의 채움을 낮춰 critical(유일한 난색·유채색·유일한 텍스처)에 시선을 양보시킨다.
// 안쪽 하이라이트는 ring 이 아니라 box-shadow 로 — `ring` 슬롯은 hover/선택(브라스)이 쓴다.
const EMPH_BAR: Record<TimelineEmphasis, string> = {
  critical:
    "bg-brass-deep bg-[repeating-linear-gradient(45deg,transparent_0px,transparent_5px,rgba(255,255,255,0.14)_5px,rgba(255,255,255,0.14)_10px)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]",
  // A/B 실측(polish-A vs polish-B): 아웃라인(/15+border)은 속이 비어 **support(채움)보다 약하게** 읽혀
  // 위계 2→3 단이 뒤집혔다 → 채움을 낮춘 /70 채택. 브라스 → 잉크 → 회색 사다리가 유지된다.
  primary: "bg-ink-soft/70 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.10)]",
  // support 는 **솔리드 유지**. /70(#d9dfe7)은 bg-paper 밴드(#edf0f5) 위에서 1.1:1 로 사라진다(polish-D 실측).
  // 위계는 채도(#c7d0dc ≪ ink/70)와 높이(12 ≪ 18)가 이미 충분히 만든다 — 여기서 더 빼면 바가 바가 아니게 된다.
  support: "bg-line-strong",
};
/** 완료 = 강조를 덮는다. 진행 여부가 종류보다 먼저 읽혀야 한다. */
const DONE_BAR = "bg-gain shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]";
/**
 * 채움색만 — 짧은 바의 캡/다이아처럼 작은 글리프용.
 * 작은 마크는 큰 면보다 **진해야** 같은 무게로 읽힌다 → 여기서는 /70 이 아니라 솔리드(마일스톤 다이아와 동일 규칙).
 */
const EMPH_FILL: Record<TimelineEmphasis, string> = {
  critical: "bg-brass-deep",
  primary: "bg-ink-soft",
  support: "bg-line-strong",
};
const EMPH_LABEL: Record<TimelineEmphasis, string> = {
  critical: "크리티컬 패스",
  primary: "주요 작업",
  support: "병행·지원",
};

const ZOOMS = [
  { key: "quarter", label: "분기" },
  { key: "year", label: "연간" },
  { key: "all", label: "전체" },
] as const;
type ZoomKey = (typeof ZOOMS)[number]["key"];

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

/** "YYYY-MM-DD" 형식 가드 — 렌더 경로에서 toDayIndex 의 throw 를 미리 막는다. */
function okDate(v: string | null | undefined): v is string {
  return typeof v === "string" && ISO_RE.test(v);
}

/** day-index 안전 변환 — 존재하지 않는 날짜(2026-02-31)는 null. 렌더가 throw 하면 화이트스크린이다. */
function dayOrNull(v: string | null | undefined): number | null {
  if (!okDate(v)) return null;
  try {
    return toDayIndex(v);
  } catch {
    return null;
  }
}

/** 바 좌표 — 깨진 날짜면 null(렌더 생략). barPct 는 반열린 [start, end+1) (결정4). */
function safeBar(start: string, end: string | null, s: Scale): { left: number; width: number } | null {
  if (!okDate(start) || !okDate(end)) return null;
  try {
    return barPct(start, end, s);
  } catch {
    return null;
  }
}

/**
 * 라벨 열의 기간 표기 — **표시 전용**(기하는 barPct 단일 출처, 여기서 날짜 산술을 하지 않는다).
 * 같은 해면 종료일의 연도를 생략한다 — 축이 이미 연도를 말하고 있고, 열 폭이 그만큼 줄어든다.
 * 깨진 날짜는 "—" (렌더 경로에서 fmtShort 의 throw = 화이트스크린).
 */
function fmtRange(start: string, end: string | null): string {
  try {
    if (!okDate(start)) return "—";
    const s = fmtShort(start);
    if (!okDate(end) || end === start) return s;
    const e = fmtShort(end);
    return s.slice(0, 2) === e.slice(0, 2) ? `${s}→${e.slice(3)}` : `${s}→${e}`;
  } catch {
    return "—";
  }
}

/** 마일스톤 좌표 안전 변환 — 깨진 날짜/throw 는 null(렌더 생략). */
function safeMs(iso: string | null | undefined, s: Scale): number | null {
  if (!okDate(iso)) return null;
  try {
    return milestonePct(iso, s);
  } catch {
    return null;
  }
}

/** 에러 메시지 추출 — repo 는 `[db] {table} {op} failed: ...` 형태로 throw 한다. */
function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** 계약이 소유하는 읽기전용 배경 축(§2.2). 드래그 핸들 없음 — 여기서 절대 UPDATE 하지 않는다. */
interface ContractAxis {
  key: string;
  label: string;
  start: string;
  end: string;
  done: boolean;
}

/** 포인터 드래그 진행 상태 — (base, 총dx)의 함수로만 해석한다. 증분 누적 금지(결정8). */
interface DragState {
  id: string;
  mode: DragMode;
  base: Span;
  startX: number;
  moved: boolean;
  span: Span;
}

/** 빈 트랙에서 끌어 만드는 신규 이벤트의 미리보기. */
interface NewDragState {
  laneKey: string;
  anchor: string;
  head: string;
  moved: boolean;
  startX: number;
}

/**
 * 신규 이벤트의 기간 — **미리보기와 커밋이 반드시 같은 식을 쓴다**.
 * 두 곳에 나눠 쓰면 임계(4px) 미만으로 날짜 경계를 넘었을 때 보이는 것과 저장되는 것이 어긋난다.
 * 임계 미만(= 클릭) = 기본 7일 바. 왼쪽으로 끈 경우 뒤집지 않고 정렬한다(결정9).
 */
function newSpan(nd: NewDragState): { start: string; end: string } {
  if (!nd.moved) return { start: nd.anchor, end: addDays(nd.anchor, NEW_DAYS - 1) };
  const a = dayOrNull(nd.anchor);
  const h = dayOrNull(nd.head);
  if (a === null || h === null) return { start: nd.anchor, end: addDays(nd.anchor, NEW_DAYS - 1) };
  return h < a ? { start: nd.head, end: nd.anchor } : { start: nd.anchor, end: nd.head };
}

/** 편집 팝오버 로컬 드래프트 — 저장 버튼에서 1회 커밋(디바운스 없음). */
interface Draft {
  label: string;
  kind: TimelineKind;
  start_date: string;
  end_date: string;
  lane_key: string;
  emphasis: TimelineEmphasis;
  done: boolean;
}

export default function TimelineBoard({
  contracts,
  deals,
  today,
  onOpenContract,
}: {
  contracts: ContractRow[];
  deals: Deal[];
  today: string;
  onOpenContract: (id: string, stage: number) => void;
}): JSX.Element {
  const [contractId, setContractId] = useState<string | null>(contracts[0]?.id ?? null);
  const [lanes, setLanes] = useState<TimelineLane[]>([]);
  const [events, setEvents] = useState<ContractTimelineEvent[]>([]);
  const [zoom, setZoom] = useState<ZoomKey>("all");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [newDrag, setNewDrag] = useState<NewDragState | null>(null);
  const [pop, setPop] = useState<{ id: string; x: number; y: number } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [undo, setUndo] = useState<{ id: string; start: string; end: string | null } | null>(null);
  const [trackEl, setTrackEl] = useState<HTMLDivElement | null>(null);
  const [trackW, setTrackW] = useState(0);

  const boardRef = useRef<HTMLDivElement>(null);
  /** events 의 ref 미러 — 커밋 시점의 rev/이전행을 setState 클로저 없이 읽는다. */
  const eventsRef = useRef<ContractTimelineEvent[]>([]);
  /** drag 의 ref 미러 — visibilitychange 가 드래그 중인지만 보면 되므로, 프레임마다 리스너를 재등록하지 않는다. */
  const dragRef = useRef<DragState | null>(null);
  /** 이벤트별 최신 요청 토큰. 지연 응답이 최신 낙관값을 덮어쓰는 것을 막는다(결정19). */
  const seqRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);

  // 계약이 1건이면 자동 선택. 선택된 계약이 목록에서 사라지면 첫 건으로 회수.
  useEffect(() => {
    if (contracts.length === 0) {
      if (contractId !== null) setContractId(null);
      return;
    }
    if (!contractId || !contracts.some((c) => c.id === contractId)) {
      setContractId(contracts[0]?.id ?? null);
    }
  }, [contracts, contractId]);

  // 레인 마스터 = 시드 DATA(DP-2). 1회 로드.
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    let alive = true;
    timelineLanesRepo
      .list()
      .then((rows) => {
        if (alive) setLanes(rows);
      })
      .catch((e) => {
        if (alive) setError(errMsg(e));
      });
    return () => {
      alive = false;
    };
  }, []);

  /** 이벤트 재로드 — 배너의 "새로고침"과 visibilitychange 가 공유한다(§7.2). */
  const reload = useCallback(async (): Promise<void> => {
    if (!contractId || !isSupabaseConfigured()) return;
    setLoading(true);
    try {
      const rows = await timelineEventsRepo.listByContract(contractId);
      setEvents(rows);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    setEvents([]);
    setPop(null);
    setUndo(null);
    void reload();
  }, [reload]);

  // 탭 2개 방어(§7.2) — 돌아오면 다시 읽는다. 3줄로 충돌 대부분을 지운다.
  useEffect(() => {
    const onVis = (): void => {
      // 드래그 중 재로드는 잡고 있는 바를 발밑에서 바꿔치기한다 → dragRef 로 건너뛴다.
      if (document.visibilityState === "visible" && !dragRef.current) void reload();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [reload]);

  // 트랙 실폭 측정 — Scale 에는 픽셀이 없다(결정6). px 는 드래그 환산에만 쓴다.
  useEffect(() => {
    if (!trackEl) return;
    const measure = (): void => setTrackW(trackEl.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(trackEl);
    return () => ro.disconnect();
  }, [trackEl]);

  const contract = useMemo(
    () => contracts.find((c) => c.id === contractId) ?? null,
    [contracts, contractId],
  );

  const dealById = useMemo(() => new Map(deals.map((d) => [d.id, d])), [deals]);

  // 계약 4축 — 읽기 전용. contracts.* 를 그리기만 한다(§2.2).
  const axes = useMemo<ContractAxis[]>(() => {
    const c = contract;
    if (!c) return [];
    const out: ContractAxis[] = [];
    const push = (key: string, label: string, s: string | null, e: string | null, done = false): void => {
      const sd = dayOrNull(s);
      const ed = dayOrNull(e);
      if (sd === null || ed === null || ed < sd) return;
      out.push({ key, label, start: s as string, end: e as string, done });
    };
    push("ct", "계약 기간", c.start_date, c.end_date);
    push("demo", `데모 선반입${c.demo_qty ? ` ${c.demo_qty}대` : ""}`, c.demo_start, c.demo_end);
    push("lic", c.license_label || "인허가", c.license_start, c.license_end, c.license_done);
    push("quota", "MOQ 쿼터", c.quota_start ?? c.start_date, c.end_date);
    return out;
  }, [contract]);

  // 이 계약의 국가에 해당하는 레인만. country_scope 빈 배열 = 전 국가 공용(§4.2).
  const visibleLanes = useMemo(() => {
    const cc = contract?.destination_country ?? "";
    return lanes
      .filter((l) => l.country_scope.length === 0 || l.country_scope.includes(cc))
      .slice()
      .sort((a, b) => a.sort - b.sort);
  }, [lanes, contract]);

  // group_label 로 그룹핑 — 레인 sort 순서를 그룹 등장 순서로 승계한다.
  const groups = useMemo(() => {
    const out: { label: string; lanes: TimelineLane[] }[] = [];
    for (const l of visibleLanes) {
      const last = out[out.length - 1];
      if (last && last.label === l.group_label) last.lanes.push(l);
      else out.push({ label: l.group_label, lanes: [l] });
    }
    return out;
  }, [visibleLanes]);

  const eventsByLane = useMemo(() => {
    const m = new Map<string, ContractTimelineEvent[]>();
    for (const e of events) {
      const arr = m.get(e.lane_key);
      if (arr) arr.push(e);
      else m.set(e.lane_key, [e]);
    }
    return m;
  }, [events]);

  // ── 스케일 ────────────────────────────────────────────────────────────────
  // 콘텐츠 실범위 → 줌 창. "전체"는 전부 담고, "연간"/"분기"는 오늘(콘텐츠 밖이면 클램프)을 앵커로 잘라 본다.
  const scale = useMemo<Scale>(() => {
    const days: number[] = [];
    for (const a of axes) {
      const s = dayOrNull(a.start);
      const e = dayOrNull(a.end);
      if (s !== null) days.push(s);
      if (e !== null) days.push(e);
    }
    for (const ev of events) {
      const s = dayOrNull(ev.start_date);
      const e = dayOrNull(ev.end_date);
      if (s !== null) days.push(s);
      if (e !== null) days.push(e);
    }
    const t = dayOrNull(today) ?? 0;
    if (days.length === 0) return makeScale(fromDayIndex(t - 15), fromDayIndex(t + 90));
    const lo = Math.min(...days) - 7;
    const hi = Math.max(...days) + 7;
    if (zoom === "all") return makeScale(fromDayIndex(lo), fromDayIndex(hi));
    const anchor = Math.min(Math.max(t, lo), hi);
    const back = zoom === "quarter" ? 15 : 30;
    const fwd = zoom === "quarter" ? 90 : 335;
    return makeScale(fromDayIndex(anchor - back), fromDayIndex(anchor + fwd));
  }, [axes, events, today, zoom]);

  const ticks = useMemo(() => {
    try {
      return monthTicks(scale, okDate(today) ? today : fromDayIndex(scale.originDay));
    } catch {
      return [];
    }
  }, [scale, today]);

  const todayPct = okDate(today) ? dateToPct(today, scale) : -1;
  const todayVisible = todayPct >= 0 && todayPct <= 100;

  // 월 밴드 — 눈금(monthTicks)의 **투영**일 뿐이다. 달 경계는 다음 눈금이, 마지막 달은 스케일 우단이 닫는다.
  // 연/분기는 라벨("26.07")에서 읽는다 — 여기서 날짜를 다시 파싱하지 않는다.
  // 방안지의 규칙: 가는 격자(월) + 굵은 격자(분기) + 가장 굵은 격자(연). 밴드는 질감, 룰이 구조다.
  const months = useMemo(() => {
    const endDay = scale.originDay + scale.spanDays;
    return ticks.map((t, i) => {
      const nextDay = i + 1 < ticks.length ? ticks[i + 1]!.dayIndex : endDay;
      const left = dayToPct(t.dayIndex, scale);
      const mo = Number(t.label.slice(3));
      return {
        key: t.label,
        label: t.label,
        left,
        width: dayToPct(nextDay, scale) - left,
        alt: i % 2 === 1,
        quarterStart: (mo - 1) % 3 === 0,
        yearStart: i > 0 && ticks[i - 1]!.label.slice(0, 2) !== t.label.slice(0, 2),
      };
    });
  }, [ticks, scale]);

  // 게이트 = **크리티컬 마일스톤**. 이 도메인에서 규제 승인 시점은 후속을 잠그는 관문이다
  // (시안: "MD-17 발급 후에만 가능"). 하드코딩한 날짜가 아니라 데이터에서 파생하는 일반 규칙.
  const gates = useMemo(() => {
    const out: { id: string; pct: number; label: string }[] = [];
    for (const e of events) {
      if (e.kind !== "milestone" || e.emphasis !== "critical") continue;
      const pct = safeMs(e.start_date, scale);
      if (pct === null || pct < 0 || pct > 100) continue;
      out.push({ id: e.id, pct, label: e.label });
    }
    return out.sort((a, b) => a.pct - b.pct);
  }, [events, scale]);

  /** 게이트 라벨은 선보다 훨씬 넓다 — 앞 라벨과 겹치면 **선만 남기고 라벨을 버린다**(선이 정보, 라벨은 편의). */
  const gateLabels = useMemo(() => {
    const out: typeof gates = [];
    let last = -99;
    for (const g of gates) {
      if (g.pct - last < 11) continue;
      out.push(g);
      last = g.pct;
    }
    return out;
  }, [gates]);

  // 헤더 모노 데이터라인 — **있는 값만** 붙인다(없는 항목을 "—" 로 채우면 그것도 소음이다).
  const dataLine = useMemo(() => {
    const p: string[] = [];
    if (okDate(contract?.start_date)) p.push(`착수 ${fmtRange(contract!.start_date as string, null)}`);
    if (okDate(contract?.end_date)) p.push(`종료 ${fmtRange(contract!.end_date as string, null)}`);
    if (contract?.destination_country) p.push(contract.destination_country);
    p.push(`항목 ${events.length}`);
    const dl = events.filter((e) => e.deal_id).length;
    if (dl > 0) p.push(`딜 ${dl}건`);
    if (gates.length > 0) p.push(`게이트 ${gates.length}`);
    return p.join("  ·  ");
  }, [contract, events, gates]);

  /** 저장 실패 = 보드 전체 읽기 전용 락(§7.1). 배너를 닫아야 다시 움직인다. */
  const locked = error !== null;

  // ── 커밋 (낙관적 + 롤백 + seq 토큰) ───────────────────────────────────────
  const patchEvent = useCallback(
    async (id: string, patch: Partial<NewTimelineEvent>): Promise<void> => {
      const prev = eventsRef.current.find((e) => e.id === id);
      if (!prev) return;
      const seq = (seqRef.current.get(id) ?? 0) + 1;
      seqRef.current.set(id, seq);
      // 낙관적 반영. rev 는 bump_timeline_rev 트리거가 정확히 +1 하므로 예측이 가능하다(§4.1)
      // → 연속 드래그가 stale rev 로 자기 자신과 충돌하지 않는다.
      setEvents((es) => es.map((e) => (e.id === id ? { ...e, ...patch, rev: e.rev + 1 } : e)));
      const timer = window.setTimeout(() => {
        if (seqRef.current.get(id) === seq) setSaving(true);
      }, SAVING_DELAY_MS);
      try {
        const saved = await timelineEventsRepo.update(id, prev.rev, patch);
        if (seqRef.current.get(id) !== seq) return; // 지연 응답 — 최신 낙관값을 덮지 않는다
        setEvents((es) => es.map((e) => (e.id === id ? saved : e)));
      } catch (e) {
        if (seqRef.current.get(id) !== seq) return;
        setEvents((es) => es.map((ev) => (ev.id === id ? prev : ev))); // 롤백 — 이전 행 전체 복원
        setError(errMsg(e));
      } finally {
        window.clearTimeout(timer);
        if (seqRef.current.get(id) === seq) setSaving(false);
      }
    },
    [],
  );

  /** 드래그 결과 커밋 + undo 1단 적재. 좌표가 그대로면 쓰지 않는다. */
  const commitSpan = useCallback(
    (id: string, next: Span): void => {
      const prev = eventsRef.current.find((e) => e.id === id);
      if (!prev) return;
      if (prev.start_date === next.start && (prev.end_date ?? null) === (next.end ?? null)) return;
      setUndo({ id, start: prev.start_date, end: prev.end_date });
      void patchEvent(id, { start_date: next.start, end_date: next.end });
    },
    [patchEvent],
  );

  // ── 포인터 (setPointerCapture — 전역 리스너 0) ────────────────────────────
  const onBarDown = (ev: ContractTimelineEvent, mode: DragMode) => (e: React.PointerEvent<HTMLElement>) => {
    if (locked || e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({
      id: ev.id,
      mode,
      base: { start: ev.start_date, end: ev.end_date },
      startX: e.clientX,
      moved: false,
      span: { start: ev.start_date, end: ev.end_date },
    });
  };

  const onBarMove = (e: React.PointerEvent<HTMLElement>): void => {
    setDrag((d) => {
      if (!d) return d;
      const dx = e.clientX - d.startX;
      if (!d.moved && Math.abs(dx) < CLICK_PX) return d; // 아직 클릭 후보
      // resolveDrag 는 (base, 총dx)의 함수 — 프레임 증분을 누적하지 않는다(결정8).
      return { ...d, moved: true, span: resolveDrag(d.mode, d.base, dx, trackW, scale) };
    });
  };

  const onBarUp = (ev: ContractTimelineEvent) => (e: React.PointerEvent<HTMLElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    const d = drag;
    setDrag(null);
    if (!d || d.id !== ev.id) return;
    if (!d.moved) {
      openPopover(ev, e.clientX, e.clientY); // 임계 미만 = 클릭 → 편집 팝오버(a11y 정식 경로)
      return;
    }
    commitSpan(ev.id, d.span); // pointerup 1회 커밋 — 드래그 중 쓰기 없음(결정16)
  };

  /** touch-action: pan-y 의 대가 — 브라우저가 스크롤을 가져가면 pointercancel 이 온다. 반드시 롤백(결정15). */
  const onBarCancel = (): void => setDrag(null);

  /**
   * 존 1개분 포인터 핸들러 묶음. **캡처와 해제가 같은 엘리먼트에서 일어나야 한다** —
   * down 을 존에 걸고 up 을 부모에서 받으면 `hasPointerCapture` 가 항상 false 라 해제가 죽은 코드가 된다
   * (브라우저의 암묵 해제에 기대게 됨). 네 핸들러를 한 묶음으로 존에 붙여 그 비대칭을 없앤다.
   */
  const zoneProps = (
    ev: ContractTimelineEvent,
    mode: DragMode,
  ): React.DOMAttributes<HTMLElement> => ({
    onPointerDown: onBarDown(ev, mode),
    onPointerMove: onBarMove,
    onPointerUp: onBarUp(ev),
    onPointerCancel: onBarCancel,
  });

  // ── 빈 트랙 → 신규 이벤트 ────────────────────────────────────────────────
  const dayAtX = useCallback(
    (clientX: number, rect: DOMRect): string => {
      const r = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
      const raw = Math.floor(r * scale.spanDays);
      const c = Math.min(Math.max(raw, 0), scale.spanDays - 1);
      return fromDayIndex(scale.originDay + c);
    },
    [scale],
  );

  const onGhostDown = (laneKey: string) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (locked || e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const at = dayAtX(e.clientX, e.currentTarget.getBoundingClientRect());
    setNewDrag({ laneKey, anchor: at, head: at, moved: false, startX: e.clientX });
  };

  const onGhostMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!newDrag) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const at = dayAtX(e.clientX, rect);
    const moved = newDrag.moved || Math.abs(e.clientX - newDrag.startX) >= CLICK_PX;
    setNewDrag({ ...newDrag, head: at, moved });
  };

  const onGhostUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    const n = newDrag;
    setNewDrag(null);
    if (!n || !contractId) return;
    const { start, end } = newSpan(n); // 미리보기와 동일한 식 — 어긋남 없음
    void createEvent(n.laneKey, start, end, e.clientX, e.clientY);
  };

  const createEvent = async (
    laneKey: string,
    start: string,
    end: string,
    x: number,
    y: number,
  ): Promise<void> => {
    if (!contractId) return;
    const row: NewTimelineEvent = {
      contract_id: contractId,
      lane_key: laneKey,
      deal_id: null,
      kind: "bar",
      label: "새 이벤트",
      start_date: start,
      end_date: end,
      emphasis: "primary",
      done: false,
      sort: 0,
      note: "",
    };
    try {
      const created = await timelineEventsRepo.create(row);
      setEvents((es) => [...es, created]);
      openPopover(created, x, y); // 레인·라벨은 팝오버에서 마무리한다(§9-Q5)
    } catch (e) {
      setError(errMsg(e));
    }
  };

  // ── 편집 팝오버 ──────────────────────────────────────────────────────────
  function openPopover(ev: ContractTimelineEvent, clientX: number, clientY: number): void {
    const rect = boardRef.current?.getBoundingClientRect();
    const x = rect ? clientX - rect.left : clientX;
    const y = rect ? clientY - rect.top : clientY;
    setDraft({
      label: ev.label,
      kind: ev.kind,
      start_date: ev.start_date,
      end_date: ev.end_date ?? ev.start_date,
      lane_key: ev.lane_key,
      emphasis: ev.emphasis,
      done: ev.done,
    });
    setPop({ id: ev.id, x, y });
  }

  const closePopover = (): void => {
    setPop(null);
    setDraft(null);
  };

  const savePopover = (): void => {
    if (!pop || !draft) return;
    const cur = eventsRef.current.find((e) => e.id === pop.id);
    if (!cur) return closePopover();
    const start = okDate(draft.start_date) ? draft.start_date : cur.start_date;
    // 기하 무결성은 DB CHECK 가 최종 판정 — UI 는 뒤집기를 애초에 만들지 않는다(결정9와 동일 문장).
    let end: string | null = null;
    if (draft.kind === "bar") {
      const e = okDate(draft.end_date) ? draft.end_date : start;
      end = (dayOrNull(e) ?? 0) < (dayOrNull(start) ?? 0) ? start : e;
    }
    if (cur.start_date !== start || (cur.end_date ?? null) !== end) {
      setUndo({ id: cur.id, start: cur.start_date, end: cur.end_date });
    }
    void patchEvent(pop.id, {
      label: draft.label.trim() || "새 이벤트",
      kind: draft.kind,
      start_date: start,
      end_date: end,
      lane_key: draft.lane_key,
      emphasis: draft.emphasis,
      // deal_id 가 있으면 done 은 deals.status 파생 — 직접 세팅 금지(DB CHECK, §3).
      done: cur.deal_id ? false : draft.done,
    });
    closePopover();
  };

  const removeEvent = async (): Promise<void> => {
    if (!pop) return;
    const id = pop.id;
    const prev = eventsRef.current.find((e) => e.id === id);
    closePopover();
    if (!prev) return;
    setEvents((es) => es.filter((e) => e.id !== id));
    try {
      await timelineEventsRepo.remove(id);
      if (undo?.id === id) setUndo(null);
    } catch (e) {
      setEvents((es) => [...es, prev]); // 롤백
      setError(errMsg(e));
    }
  };

  const runUndo = (): void => {
    if (!undo) return;
    void patchEvent(undo.id, { start_date: undo.start, end_date: undo.end });
    setUndo(null);
  };

  // ── 빈 상태 ──────────────────────────────────────────────────────────────
  if (contracts.length === 0 || !contract) {
    return (
      <div className="rounded-lg border border-line bg-surface px-5 py-12 text-center shadow-[0_1px_2px_rgba(16,30,48,0.06)]">
        <div className="mx-auto mb-3 h-px w-8 bg-brass" />
        <h3 className="font-display text-[16px] font-semibold tracking-tight text-graphite">계약을 먼저 등록하세요</h3>
        <p className="mx-auto mt-2 max-w-[440px] text-[11.5px] leading-relaxed text-graphite-mut">
          타임라인은 <b className="font-semibold text-graphite">계약(런칭 프로그램)</b>에 매달립니다. 계약을
          등록하면 계약기간·데모·인허가·MOQ 쿼터가 읽기전용 배경 레인으로 깔리고, 그 위에 규제·생산·교육·마케팅
          이벤트를 직접 끌어 그릴 수 있습니다.
        </p>
      </div>
    );
  }

  const dragging = drag;

  return (
    // overflow-hidden 금지 — 편집 팝오버가 아래쪽 행에서 잘린다. 클리핑은 필요한 자식(스크롤 영역)이 각자 한다.
    <div
      ref={boardRef}
      className="relative mb-6 rounded-lg border border-line bg-surface shadow-[0_1px_2px_rgba(16,30,48,0.06)]"
    >
      {/* 헤더 — eyebrow / 계약명 / 모노 데이터라인 · 우측 = 계기(줌·undo) */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-line px-5 pb-4 pt-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <i className="h-px w-4 shrink-0 bg-brass" />
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-graphite-mut">
              프로그램 타임라인
            </span>
          </div>
          <h3 className="mt-1.5 truncate font-display text-[18px] font-semibold tracking-tight text-graphite">
            {contract.name}
          </h3>
          <p className="mt-1.5 font-mono text-[10.5px] tabular-nums text-graphite-mut">{dataLine}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {saving && <span className="font-mono text-[10.5px] text-graphite-mut">저장중…</span>}
          {loading && <span className="font-mono text-[10.5px] text-graphite-mut">불러오는 중…</span>}
          {contracts.length > 1 && (
            <select
              value={contractId ?? ""}
              onChange={(e) => setContractId(e.target.value)}
              className="max-w-[240px] rounded border border-line bg-surface px-2 py-1 text-xs text-graphite focus:border-brass focus:outline-none"
            >
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={runUndo}
            disabled={!undo || locked}
            className={btn + (undo && !locked ? "" : " opacity-40")}
            title="마지막 이동 되돌리기"
          >
            ↺ 되돌리기
          </button>
          {/* 줌 세그먼티드 — 계기 눈금 셀렉터. 모노 = 계측 단위. */}
          <div className="flex overflow-hidden rounded-[5px] border border-line">
            {ZOOMS.map((z) => (
              <button
                key={z.key}
                type="button"
                onClick={() => setZoom(z.key)}
                className={
                  "border-l border-line px-2.5 py-1 font-mono text-[10.5px] tracking-wide first:border-l-0 " +
                  (zoom === z.key
                    ? "bg-ink font-semibold text-onink"
                    : "bg-surface text-graphite-mut hover:bg-surface-2")
                }
              >
                {z.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 저장 실패 = 빨간 배너 + 보드 락 (§7.1 — 회색 토스트 금지) */}
      {error && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-risk/30 bg-risk-tint px-4 py-2">
          <p className="min-w-0 text-[11.5px] text-risk">
            <b className="font-semibold">보드가 잠겼습니다.</b> {error}
          </p>
          <button type="button" onClick={() => void reload()} className={btn}>
            새로고침
          </button>
        </div>
      )}

      {/* 축 헤더 — 좌: 조작 한 줄 / 우: 범례. 범례 글리프는 **보드의 실제 형태와 같다**(§8.2).
          의미색은 글리프가 운반하고 텍스트는 무채색 — 유색 마이크로텍스트 금지. */}
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-1.5 border-b border-line bg-surface-2 px-5 py-2">
        <p className="text-[10px] text-graphite-mut">
          드래그 이동 · 양끝 기간 조절 · 빈 트랙 새 이벤트 · 클릭 편집
          <span className="mx-1.5 text-line-strong">|</span>
          계약 4축 읽기 전용
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-graphite-mut">
          {/* 글리프는 보드의 채움·높이 비율(22/18/12)을 그대로 축약한다 — 범례가 보드와 다른 말을 하면 안 된다. */}
          <Legend glyph={<i className="h-[7px] w-4 rounded-[2px] bg-brass-deep" />} label="크리티컬" />
          <Legend glyph={<i className="h-[5px] w-4 rounded-[2px] bg-ink-soft/70" />} label="주요" />
          <Legend glyph={<i className="h-[4px] w-4 rounded-[2px] bg-line-strong" />} label="지원" />
          <Legend glyph={<i className="h-[5px] w-4 rounded-[2px] bg-gain" />} label="완료" />
          <Legend glyph={<i className="h-2 w-2 rotate-45 rounded-[1px] bg-ink-soft" />} label="마일스톤" />
          <Legend glyph={<i className="h-3 w-0 border-l border-dashed border-brass-deep" />} label="게이트" />
          <Legend
            glyph={<i className="h-3 w-[1.5px] bg-brass" />}
            label={`오늘 ${fmtRange(okDate(today) ? today : fromDayIndex(scale.originDay), null)}`}
          />
          <Legend glyph={<LockGlyph />} label="계약 소유" />
        </div>
      </div>

      {/* 보드 본체 */}
      <div className="overflow-x-auto px-5 pb-5 pt-2">
        <div className="relative min-w-[960px]">
          {/* 방안지 — 월 밴드(surface/surface-2 교대) + 월 경계 hairline + 연 경계 line-strong.
              눈금 헤더까지 관통해 보드 전체 높이로 깔린다. 행은 배경이 없어 그대로 비친다(z-0). */}
          <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-0" style={{ left: LABEL_W }}>
            <div className="relative h-full w-full">
              {months.map((m) => (
                <div
                  key={m.key}
                  className={
                    "absolute bottom-0 top-0 " +
                    (m.alt ? "bg-paper " : "") +
                    (m.yearStart
                      ? "border-l-2 border-line-strong"
                      : m.quarterStart
                        ? "border-l border-line-strong"
                        : "border-l border-line")
                  }
                  style={{ left: `${m.left}%`, width: `${m.width}%` }}
                />
              ))}
              {/* 라벨 열과 캔버스를 가르는 기준선(datum). */}
              <div className="absolute bottom-0 left-0 top-0 w-px bg-line-strong" />
            </div>
          </div>

          {/* 계기 오버레이 — 게이트 점선 + 오늘선. 눈금 헤더 아래에서만 그린다(월 라벨을 긋지 않는다).
              게이트 스트립 위에서 멈춘다 — 선이 라벨을 관통하면 그 라벨은 못 읽는다. 스트립의 border-t 가 선을 이어받는다. */}
          <div
            className="pointer-events-none absolute right-0 z-0"
            style={{ left: LABEL_W, top: RULER_H, bottom: gateLabels.length > 0 ? GATE_H + 5 : 0 }}
          >
            <div className="relative h-full w-full">
              {/* 게이트 — 오늘선보다 약하게, 뒤에. 후속 작업을 잠그는 관문을 보드 전체로 관통시킨다. */}
              {gates.map((g) => (
                <div
                  key={g.id}
                  className="absolute bottom-0 top-0 border-l border-dashed border-brass-deep/45"
                  style={{ left: `${g.pct}%` }}
                />
              ))}
              {todayVisible && (
                <div className="absolute bottom-0 top-0 w-[1.5px] bg-brass" style={{ left: `${todayPct}%` }} />
              )}
            </div>
          </div>

          {/* 눈금 헤더 — 상단=오늘 칩 / 하단=월 라벨. 두 단으로 갈라 서로 겹치지 않게 한다. */}
          <div className="relative z-[1] flex items-end border-b border-line-strong" style={{ height: RULER_H }}>
            <div
              className="shrink-0 pb-1.5 pr-4 text-right font-mono text-[9.5px] tabular-nums text-graphite-mut"
              style={{ width: LABEL_W }}
            >
              {fmtRange(fromDayIndex(scale.originDay), null)} →{" "}
              {fmtRange(fromDayIndex(scale.originDay + scale.spanDays - 1), null)}
            </div>
            <div ref={setTrackEl} className="relative h-full min-w-0 flex-1">
              {months.map((m) => (
                <span
                  key={m.key}
                  className={
                    "absolute bottom-1 pl-1.5 font-mono text-[10px] tabular-nums " +
                    (m.yearStart ? "font-semibold text-graphite" : "text-graphite-mut")
                  }
                  style={{ left: `${m.left}%` }}
                >
                  {m.label}
                </span>
              ))}
              {todayVisible && (
                // brass 위 흰 텍스트는 3.26:1 로 미달 → 칩은 brass-deep(5.21:1). brass 는 선이 맡는다.
                <span
                  className="absolute top-0 z-[2] w-[30px] rounded-[2px] bg-brass-deep text-center font-mono text-[9px] font-bold leading-[13px] text-white"
                  style={{ left: `min(max(${todayPct}% - 15px, 0px), calc(100% - 30px))` }}
                >
                  오늘
                </span>
              )}
            </div>
          </div>

          <div className="relative z-[1]">
            {/* ── 계약 4축 — 읽기 전용 배경 레인(§2.2). 핸들 없음. 클릭 = 계약 카드 딥링크. ── */}
            {axes.length > 0 && (
              <>
                <GroupHead label="계약 (읽기 전용)" />
                {axes.map((a) => {
                  const g = safeBar(a.start, a.end, scale);
                  return (
                    <div key={a.key} className="group flex items-center" style={{ height: ROW_H }}>
                      <div className="flex shrink-0 items-center gap-1.5 pr-4" style={{ width: LABEL_W }}>
                        <LockGlyph />
                        <span className="min-w-0 flex-1 truncate text-[12px] text-graphite-mut">{a.label}</span>
                        {a.done && <i className="h-1.5 w-1.5 shrink-0 rounded-full bg-gain" title="완료" />}
                        <span
                          className="shrink-0 text-right font-mono text-[10px] tabular-nums text-graphite-mut"
                          style={{ width: DATE_W }}
                        >
                          {fmtRange(a.start, a.end)}
                        </span>
                      </div>
                      <div className="relative h-full min-w-0 flex-1 overflow-hidden">
                        {g && (
                          // 배경 레인 = 가장 낮은 위계(14px). p-0 은 global.css 의 bare `button` 패딩을 무력화한다.
                          <button
                            type="button"
                            onClick={() => onOpenContract(contract.id, 2)}
                            title={`${a.label}: ${fmtShort(a.start)} ~ ${fmtShort(a.end)} · 계약 카드에서 수정`}
                            className="absolute top-1/2 h-[14px] -translate-y-1/2 rounded-[3px] border border-line-strong bg-line-strong/40 p-0 ring-brass group-hover:ring-1"
                            style={{ left: `${g.left}%`, width: `${g.width}%` }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* ── 레인 그룹 · 이벤트 ── */}
            {groups.map((grp) => (
              <div key={grp.label || "_"}>
                {/* 레인이 하나뿐이고 그 이름이 그룹명과 같으면 헤더는 같은 말을 두 번 하는 것이다.
                    레인 행은 신규 이벤트 트랙을 겸해 지울 수 없으므로, 장식인 그룹 헤더를 접는다.
                    (시안은 그룹→이벤트 2단. 3단이 필요한 것은 레인이 2개인 '규제' 뿐이다.) */}
                {!(grp.lanes.length === 1 && grp.lanes[0]!.label_ko === grp.label) && (
                  <GroupHead label={grp.label || "기타"} />
                )}
                {grp.lanes.map((lane) => {
                  const rows = eventsByLane.get(lane.lane_key) ?? [];
                  const nd = newDrag?.laneKey === lane.lane_key ? newDrag : null;
                  const ndSpan = nd ? newSpan(nd) : null;
                  const ndBar = ndSpan ? safeBar(ndSpan.start, ndSpan.end, scale) : null;
                  return (
                    <div key={lane.lane_key}>
                      {/* 레인 헤더 행 = 신규 이벤트 트랙.
                          점선 트랙을 상시 그리면 레인 수만큼 점선 소음이 쌓여 정작 바가 안 보인다
                          → 평소엔 극도로 흐린 baseline 만, 어포던스는 **행 호버에서만** 꺼낸다(생성 로직은 그대로). */}
                      <div className="group flex items-center" style={{ height: ROW_H }}>
                        <div
                          className="shrink-0 truncate pr-4 text-[11px] font-semibold tracking-tight text-graphite-mut"
                          style={{ width: LABEL_W }}
                          title={lane.note || lane.label_ko}
                        >
                          {lane.label_ko}
                        </div>
                        <div
                          onPointerDown={onGhostDown(lane.lane_key)}
                          onPointerMove={onGhostMove}
                          onPointerUp={onGhostUp}
                          onPointerCancel={() => setNewDrag(null)}
                          className={
                            "relative h-full min-w-0 flex-1 select-none touch-pan-y overflow-hidden " +
                            (locked ? "cursor-not-allowed" : "cursor-copy")
                          }
                          title={locked ? "보드 잠김" : `${lane.label_ko} — 클릭·드래그로 이벤트 생성`}
                        >
                          <div className="pointer-events-none absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line/70" />
                          <div className="pointer-events-none absolute inset-x-0 top-1/2 h-[18px] -translate-y-1/2 rounded-[3px] border border-dashed border-line-strong opacity-0 transition-opacity duration-100 group-hover:opacity-100" />
                          {!nd && !locked && (
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center font-mono text-[10px] text-graphite-mut opacity-0 transition-opacity duration-100 group-hover:opacity-100">
                              + 새 이벤트
                            </div>
                          )}
                          {ndBar && (
                            <div
                              className="pointer-events-none absolute top-1/2 h-[18px] -translate-y-1/2 rounded-[3px] border border-brass bg-brass-tint"
                              style={{ left: `${ndBar.left}%`, width: `${ndBar.width}%` }}
                            />
                          )}
                        </div>
                      </div>

                      {/* 이벤트 행 */}
                      {rows.map((ev) => {
                        const live = dragging?.id === ev.id && dragging.moved ? dragging.span : null;
                        const start = live ? live.start : ev.start_date;
                        const end = live ? live.end : ev.end_date;
                        const deal = ev.deal_id ? dealById.get(ev.deal_id) : undefined;
                        // 편집 팝오버가 열렸거나 잡고 있는 바 = 선택 상태(브라스 링). 별도 selection state 를 만들지 않는다.
                        const sel = pop?.id === ev.id || dragging?.id === ev.id;
                        return (
                          <div key={ev.id} className="group flex items-center" style={{ height: ROW_H }}>
                            <div className="flex shrink-0 items-center gap-1.5 pl-3 pr-4" style={{ width: LABEL_W }}>
                              {ev.done && <i className="h-1.5 w-1.5 shrink-0 rounded-full bg-gain" title="완료" />}
                              <span className="min-w-0 flex-1 truncate text-[12px] text-graphite" title={ev.label}>
                                {ev.label}
                              </span>
                              {deal && (
                                <span
                                  className="shrink-0 rounded-[3px] border border-line bg-surface-2 px-1 font-mono text-[9px] text-graphite-mut"
                                  title={`딜 링크 — 진행상태는 ${deal.status} (deals.status 단일 출처)`}
                                >
                                  {deal.deal_no}
                                </span>
                              )}
                              {/* 기간 = 바에서 뽑아낸 계측값. 드래그 중에는 live 좌표를 그대로 읽는다(바 = 형태, 여기 = 수치). */}
                              <span
                                className="shrink-0 text-right font-mono text-[10px] tabular-nums text-graphite-mut"
                                style={{ width: DATE_W }}
                              >
                                {fmtRange(start, ev.kind === "milestone" ? null : end)}
                              </span>
                            </div>
                            <div className="relative h-full min-w-0 flex-1 overflow-hidden">
                              {ev.kind === "milestone"
                                ? renderMilestone(ev, start, scale, locked, sel, (m) => zoneProps(ev, m))
                                : renderBar(ev, start, end, scale, trackW, locked, sel, (m) => zoneProps(ev, m))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}

            {visibleLanes.length === 0 && (
              <p className="px-1 py-6 text-center text-[11.5px] text-graphite-mut">
                이 계약의 국가({contract.destination_country || "—"})에 해당하는 레인이 없습니다. 레인은 시드
                DATA(timeline_lanes)입니다.
              </p>
            )}

            {/* 게이트 라벨 스트립 — 도면의 치수선처럼 축 하단에 붙는다. 오늘 칩(상단)과 층이 달라 겹치지 않는다. */}
            {gateLabels.length > 0 && (
              <div className="mt-1 flex items-center border-t border-line" style={{ height: GATE_H }}>
                <div
                  className="shrink-0 pr-4 text-right font-mono text-[9px] uppercase tracking-[0.14em] text-graphite-mut"
                  style={{ width: LABEL_W }}
                >
                  게이트
                </div>
                <div className="relative h-full min-w-0 flex-1">
                  {gateLabels.map((g) => (
                    <span
                      key={g.id}
                      title={g.label}
                      className="absolute top-1/2 w-[96px] -translate-y-1/2 truncate text-center font-mono text-[9px] text-graphite"
                      style={{ left: `min(max(${g.pct}% - 48px, 0px), calc(100% - 96px))` }}
                    >
                      {g.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 편집 팝오버 — a11y 정식 경로(§5.2 결정20). 키보드 드래그를 a11y 라 우기지 않는다. */}
      {pop && draft && (
        <EditPopover
          x={pop.x}
          y={pop.y}
          boardW={boardRef.current?.clientWidth ?? 0}
          draft={draft}
          lanes={visibleLanes}
          dealLinked={!!eventsRef.current.find((e) => e.id === pop.id)?.deal_id}
          onChange={setDraft}
          onSave={savePopover}
          onDelete={() => void removeEvent()}
          onClose={closePopover}
        />
      )}
    </div>
  );
}

// ── 조각들 ──────────────────────────────────────────────────────────────────

/**
 * 레인 그룹 헤더 — 시안의 그룹 캡션.
 * 레인 행(11px semibold)과 톤이 겹치면 위계가 뭉갠다 → 도면의 섹션 캡션처럼 **작게·넓게 + 라벨 열까지 긋는 룰**.
 * 룰을 라벨 열 폭으로 가두는 이유: 캔버스로 새어 나가면 월 밴드를 가로지르는 또 하나의 소음이 된다.
 */
function GroupHead({ label }: { label: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 pb-1.5 pt-4" style={{ width: LABEL_W }}>
      <span className="whitespace-nowrap font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-graphite-mut">
        {label}
      </span>
      <i className="h-px min-w-0 flex-1 bg-line-strong" />
    </div>
  );
}

/** 범례 1항 — 글리프가 의미를 운반하고 텍스트는 무채색으로 남는다(§8.2). */
function Legend({ glyph, label }: { glyph: JSX.Element; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {glyph}
      {label}
    </span>
  );
}

/** 자물쇠 — 계약 소유(읽기 전용) 표식. 의미색 없이 형태로만 말한다. */
function LockGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 12 12" className="h-3 w-3 shrink-0 fill-graphite-mut" aria-hidden="true">
      <path d="M3.4 5.1V3.7a2.6 2.6 0 0 1 5.2 0v1.4h.3c.4 0 .7.3.7.7v4c0 .4-.3.7-.7.7H3a.7.7 0 0 1-.7-.7v-4c0-.4.3-.7.7-.7h.4Zm1.3 0h2.6V3.7a1.3 1.3 0 0 0-2.6 0v1.4Z" />
    </svg>
  );
}

/**
 * 바 렌더 — 보이는 바는 **실폭**(최소폭 클램프 금지, §7.4), 히트영역만 투명하게 최소폭을 갖는다.
 * 히트영역은 3존 flex(좌핸들/본체/우핸들) — hitZone 계산함수가 필요 없다. DOM 이 히트테스트한다(결정13).
 * barPx < 16 이면 핸들을 **제거**한다 — 빗맞은 리사이즈보다 어포던스 제거가 옳다(결정14).
 * 바 안에 텍스트는 없다 — 기간은 라벨 열의 데이터 열이 맡고 툴팁(title)이 전문을 갖는다.
 * 링은 히트영역이 아니라 **보이는 바**가 두른다(높이가 강조마다 다르므로) — group-hover 는 행에서 온다.
 */
function renderBar(
  ev: ContractTimelineEvent,
  start: string,
  end: string | null,
  scale: Scale,
  trackW: number,
  locked: boolean,
  selected: boolean,
  zone: (mode: DragMode) => React.DOMAttributes<HTMLElement>,
): JSX.Element | null {
  const g = safeBar(start, end, scale);
  if (!g) return null;
  const barPx = (g.width / 100) * trackW;
  const handles = barPx >= HANDLE_MIN_PX;
  const title = `${ev.label} · ${fmtShort(start)} ~ ${fmtShort(end ?? start)}${ev.note ? ` · ${ev.note}` : ""}`;
  // 일수 = barPct 의 역산(width% × spanDays = end+1−start). 날짜 산술을 재구현하지 않는다(결정1·7).
  const days = Math.round((g.width / 100) * scale.spanDays);
  const short = days > 0 && days <= SHORT_BAR_DAYS;
  const ring = selected ? " ring-2 ring-brass" : " ring-brass group-hover:ring-1";
  const solid = ev.done ? "bg-gain" : EMPH_FILL[ev.emphasis];
  return (
    <>
      {/* 보이는 바 — 실폭(최소폭 클램프 없음). 포인터는 아래 히트영역이 받는다. */}
      {short ? (
        // 짧은 바 = 캡(실제 기간 — 기하의 진실을 버리지 않는다) + 다이아(판독성). 데이터는 그대로 bar 다.
        <div
          className="pointer-events-none absolute top-1/2 -translate-y-1/2"
          style={{ left: `${g.left}%`, width: `${g.width}%`, height: EMPH_H[ev.emphasis] }}
        >
          <div className={"absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-[1px] " + solid} />
          <i
            className={
              "absolute left-1/2 top-1/2 h-[10px] w-[10px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[1px] shadow-[0_0_0_2px_#ffffff] " +
              solid +
              ring
            }
          />
        </div>
      ) : (
        <div
          className={
            "pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-[3px] " +
            (ev.done ? DONE_BAR : EMPH_BAR[ev.emphasis]) +
            ring
          }
          style={{ left: `${g.left}%`, width: `${g.width}%`, height: EMPH_H[ev.emphasis] }}
        />
      )}
      {/* 투명 히트영역 — 최소폭은 여기서만(§7.4). 존마다 핸들러 묶음 = 캡처/해제 대칭. */}
      <div
        title={title}
        className={"absolute top-1/2 flex h-[22px] -translate-y-1/2 select-none touch-pan-y " + (locked ? "cursor-not-allowed" : "")}
        style={{ left: `${g.left}%`, width: `${g.width}%`, minWidth: 12 }}
      >
        {handles && (
          <div
            {...zone("resize-start")}
            className={"w-2 shrink-0 " + (locked ? "" : "cursor-ew-resize")}
            title="시작일 조절"
          />
        )}
        <div
          {...zone("move")}
          className={"min-w-0 flex-1 " + (locked ? "" : "cursor-grab active:cursor-grabbing")}
        />
        {handles && (
          <div
            {...zone("resize-end")}
            className={"w-2 shrink-0 " + (locked ? "" : "cursor-ew-resize")}
            title="종료일 조절"
          />
        )}
      </div>
    </>
  );
}

/**
 * 마일스톤 — 셀 중앙 다이아몬드(결정5). move 만 유효(리사이즈 핸들 없음).
 * 흰 분리링은 box-shadow 로 — `ring` 슬롯은 hover/선택(브라스)이 쓴다. 월 밴드 위에서 다이아가 묻히지 않게 한다.
 * 날짜 텍스트는 라벨 열의 데이터 열로 옮겼다(캔버스에는 형태만 남는다).
 */
function renderMilestone(
  ev: ContractTimelineEvent,
  start: string,
  scale: Scale,
  locked: boolean,
  selected: boolean,
  zone: (mode: DragMode) => React.DOMAttributes<HTMLElement>,
): JSX.Element | null {
  const pct = safeMs(start, scale);
  if (pct === null) return null;
  const fill = ev.done ? "bg-gain" : ev.emphasis === "critical" ? "bg-brass-deep" : "bg-ink-soft";
  return (
    <>
      <i
        className={
          "pointer-events-none absolute top-1/2 h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[1px] shadow-[0_0_0_2px_#ffffff] " +
          fill +
          (selected ? " ring-2 ring-brass" : " ring-brass group-hover:ring-1")
        }
        style={{ left: `${pct}%` }}
      />
      {/* 마일스톤은 move 만 — resize 핸들 자체를 렌더하지 않는다(resolveSpan 도 무변화로 막는다). */}
      <div
        {...zone("move")}
        title={`${ev.label} · ${fmtShort(start)} (마일스톤)`}
        className={
          "absolute top-1/2 h-[22px] w-[18px] -translate-x-1/2 -translate-y-1/2 select-none touch-pan-y " +
          (locked ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing")
        }
        style={{ left: `${pct}%` }}
      />
    </>
  );
}

/** 편집 팝오버 — 로컬 드래프트 + 저장 1회 커밋(디바운스 없음). 날짜는 date input = 키보드 정식 경로. */
function EditPopover({
  x,
  y,
  boardW,
  draft,
  lanes,
  dealLinked,
  onChange,
  onSave,
  onDelete,
  onClose,
}: {
  x: number;
  y: number;
  boardW: number;
  draft: Draft;
  lanes: TimelineLane[];
  dealLinked: boolean;
  onChange: (d: Draft) => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
}): JSX.Element {
  const W = 268;
  const left = boardW > 0 ? Math.min(Math.max(x - W / 2, 8), Math.max(boardW - W - 8, 8)) : Math.max(x - W / 2, 8);
  const top = Math.max(y + 14, 8);
  const fld = "w-full rounded border border-line bg-surface px-2 py-1 text-xs text-graphite focus:border-brass focus:outline-none";
  return (
    <div
      className="absolute z-20 rounded-lg border border-line-strong bg-surface p-3 shadow-[0_6px_20px_rgba(16,30,48,0.14)]"
      style={{ left, top, width: W }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSave();
      }}
    >
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-[11.5px] font-bold text-graphite">이벤트 편집</h4>
        <button type="button" onClick={onClose} className="text-[11px] text-graphite-mut hover:text-graphite">
          닫기 ✕
        </button>
      </div>

      <label className="mb-2 block">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-graphite-mut">라벨</span>
        <input
          autoFocus
          value={draft.label}
          onChange={(e) => onChange({ ...draft, label: e.target.value })}
          className={fld}
        />
      </label>

      <div className="mb-2 grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-graphite-mut">종류</span>
          <select
            value={draft.kind}
            onChange={(e) => onChange({ ...draft, kind: e.target.value as TimelineKind })}
            className={fld}
          >
            <option value="bar">바 (기간)</option>
            <option value="milestone">마일스톤 (시점)</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-graphite-mut">강조</span>
          <select
            value={draft.emphasis}
            onChange={(e) => onChange({ ...draft, emphasis: e.target.value as TimelineEmphasis })}
            className={fld}
          >
            {(["critical", "primary", "support"] as TimelineEmphasis[]).map((k) => (
              <option key={k} value={k}>
                {EMPH_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mb-2 grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-graphite-mut">시작일</span>
          <input
            type="date"
            value={draft.start_date}
            onChange={(e) => onChange({ ...draft, start_date: e.target.value })}
            className={fld}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-graphite-mut">종료일</span>
          <input
            type="date"
            value={draft.kind === "milestone" ? "" : draft.end_date}
            disabled={draft.kind === "milestone"}
            onChange={(e) => onChange({ ...draft, end_date: e.target.value })}
            className={fld + (draft.kind === "milestone" ? " opacity-40" : "")}
          />
        </label>
      </div>

      <label className="mb-2 block">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-graphite-mut">레인</span>
        <select
          value={draft.lane_key}
          onChange={(e) => onChange({ ...draft, lane_key: e.target.value })}
          className={fld}
        >
          {lanes.map((l) => (
            <option key={l.lane_key} value={l.lane_key}>
              {l.group_label ? `${l.group_label} · ${l.label_ko}` : l.label_ko}
            </option>
          ))}
        </select>
      </label>

      <label
        className="mb-3 flex items-center gap-2 text-[11.5px] text-graphite"
        title={dealLinked ? "딜 링크 이벤트의 완료는 deals.status 에서 파생됩니다 (직접 세팅 금지)" : undefined}
      >
        <input
          type="checkbox"
          checked={draft.done}
          disabled={dealLinked}
          onChange={(e) => onChange({ ...draft, done: e.target.checked })}
          className="h-3.5 w-3.5 accent-ink"
        />
        <span className={dealLinked ? "text-graphite-mut" : ""}>완료</span>
        {dealLinked && <span className="text-[10px] text-graphite-mut">· 딜 상태에서 파생</span>}
      </label>

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onDelete}
          className="rounded border border-risk/40 bg-surface px-2 py-1 text-xs text-risk hover:bg-risk-tint"
        >
          삭제
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className={btn}>
            취소
          </button>
          <button
            type="button"
            onClick={onSave}
            className="rounded border border-ink bg-ink px-2.5 py-1 text-xs font-semibold text-onink hover:bg-ink-soft"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
}
